import React from 'react';
import { Mail, Github, Linkedin, Globe, Settings, Trash2, MessageCircle } from 'lucide-react';

export default function HeroSection({
    t,
    portfolio,
    currentLang,
    getText,
    isAdmin,
    editingPersonal,
    setEditingPersonal,
    tempPersonal,
    setTempPersonal,
    savePersonalInfo,
    handleFileSelect,
    uploadingPhoto,
    saving,
    scrollToSection
}) {
    const phoneNumber = portfolio.personal.phone ? portfolio.personal.phone.replace(/\D/g, '') : '';
    const whatsappUrl = phoneNumber ? `https://wa.me/${phoneNumber}` : '#';

    return (
        <header id={t.nav[0].toLowerCase().replace(/\s+/g, '-')} className="pt-32 pb-4 flex flex-col items-center justify-center px-4 relative overflow-hidden">
            {/* Background Blobs */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

            <div className="max-w-5xl mx-auto text-center z-10 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out">
                <div className="relative inline-block mb-8 group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                    <div className="relative w-40 h-40 rounded-full bg-slate-900 flex items-center justify-center border-4 border-slate-800 overflow-hidden">
                        {portfolio.personal.photo_url ? (
                            <img
                                src={portfolio.personal.photo_url}
                                alt={portfolio.personal.name}
                                className="w-full h-full object-cover"
                                fetchPriority="high"
                                loading="eager"
                            />
                        ) : (
                            <span className="text-6xl font-black bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                                {portfolio.personal.name ? portfolio.personal.name.split(' ').map(n => n[0]).join('') : 'JD'}
                            </span>
                        )}
                    </div>
                </div>

                <h1 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent transform hover:scale-105 transition-transform duration-500 pb-2">
                    {portfolio.personal.name}
                </h1>

                <p className="text-xl md:text-3xl text-slate-300 mb-10 font-light text-justify md:text-center max-w-4xl mx-auto leading-relaxed">
                    {getText(portfolio.personal.title, currentLang)}
                </p>

                {/* Hero CTA */}
                <div className="flex flex-wrap justify-center gap-6 mb-12">
                    <button
                        onClick={() => scrollToSection(t.nav[4].toLowerCase().replace(/\s+/g, '-'))}
                        className="group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-4 rounded-xl transition-all duration-300 flex items-center gap-3 font-bold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:-translate-y-1"
                    >
                        <Mail className="group-hover:scale-110 transition-transform" size={24} />
                        <span>{t.contactMe || 'Contáctame'}</span>
                    </button>
                    {phoneNumber && (
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group bg-green-600/10 hover:bg-green-600/20 border border-green-600/50 hover:border-green-500 px-6 py-4 rounded-xl transition-all duration-300 flex items-center gap-3 font-bold hover:-translate-y-1"
                        >
                            <MessageCircle className="text-green-500 group-hover:text-green-400 transition-colors" size={24} />
                            <span className="text-green-500 group-hover:text-green-400">WhatsApp</span>
                        </a>
                    )}
                    <div className="flex gap-4">
                        {[
                            { Icon: Github, href: portfolio.personal.github, label: 'GitHub' },
                            { Icon: Linkedin, href: portfolio.personal.linkedin, label: 'LinkedIn' },
                            { Icon: Globe, href: portfolio.personal.website, label: 'Website' }
                        ].map(({ Icon, href, label }, idx) => (
                            <a
                                key={idx}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 hover:border-cyan-500 hover:bg-cyan-500/10 hover:text-cyan-400 transition-all duration-300 hover:-translate-y-1"
                                aria-label={label}
                            >
                                <Icon size={24} />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Admin Personal Info Edit */}
                {isAdmin && (
                    <div className="mb-8">
                        <button
                            onClick={() => {
                                setTempPersonal({ ...portfolio.personal });
                                setEditingPersonal(true);
                            }}
                            className="bg-purple-600/20 text-purple-400 border border-purple-500/50 px-6 py-2 rounded-full hover:bg-purple-600 hover:text-white transition-all duration-300 inline-flex items-center gap-2"
                        >
                            <Settings size={18} />
                            {t.editInfo}
                        </button>
                    </div>
                )}
                {editingPersonal && (
                    <div className="bg-slate-800/90 backdrop-blur-md rounded-2xl p-8 mb-8 text-left max-w-2xl mx-auto border border-slate-700 shadow-2xl animate-fade-in-up">
                        <h3 className="text-xl font-bold mb-6 text-white border-b border-slate-700 pb-2">Editar Información Personal</h3>
                        <div className="space-y-4">
                            <input
                                type="text"
                                placeholder="Nombre completo"
                                value={tempPersonal.name}
                                onChange={(e) => setTempPersonal({ ...tempPersonal, name: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all"
                            />
                            {/* Add inputs for title (es, pt, en) */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <input type="text" placeholder="Cargo (ES)" value={tempPersonal.title.es} onChange={(e) => setTempPersonal({ ...tempPersonal, title: { ...tempPersonal.title, es: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm" />
                                <input type="text" placeholder="Cargo (PT)" value={tempPersonal.title.pt} onChange={(e) => setTempPersonal({ ...tempPersonal, title: { ...tempPersonal.title, pt: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm" />
                                <input type="text" placeholder="Cargo (EN)" value={tempPersonal.title.en} onChange={(e) => setTempPersonal({ ...tempPersonal, title: { ...tempPersonal.title, en: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input type="email" placeholder="Email" value={tempPersonal.email} onChange={(e) => setTempPersonal({ ...tempPersonal, email: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3" />
                                <input type="text" placeholder="Teléfono" value={tempPersonal.phone} onChange={(e) => setTempPersonal({ ...tempPersonal, phone: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3" />
                            </div>
                            {/* Profile Photo Section */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-400">Foto de Perfil</label>

                                <div className="flex items-center gap-6 p-4 bg-slate-900 border border-slate-700 rounded-xl">
                                    {/* Current/Preview */}
                                    <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-slate-600 bg-slate-800 flex-shrink-0">
                                        {tempPersonal.photo_url ? (
                                            <img src={tempPersonal.photo_url} alt="Profile Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-500">
                                                <span className="text-2xl font-bold">{tempPersonal.name?.charAt(0)}</span>
                                            </div>
                                        )}
                                        {uploadingPhoto && (
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex-1">
                                        <div className="flex gap-2">
                                            <label className={`cursor-pointer bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
                                                <span>{uploadingPhoto ? 'Subiendo...' : 'Subir Imagen'}</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={handleFileSelect}
                                                />
                                            </label>
                                            {tempPersonal.photo_url && (
                                                <button
                                                    onClick={() => setTempPersonal({ ...tempPersonal, photo_url: '' })}
                                                    className="bg-red-600/20 text-red-400 hover:bg-red-600/40 px-3 py-2 rounded-lg text-sm transition-colors"
                                                    title="Eliminar foto"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 mt-2">Formatos: JPG, PNG, GIF. Máx 2MB.</p>
                                    </div>
                                </div>
                            </div>
                            {/* Bio editors */}
                            <div className="space-y-2">
                                <textarea placeholder="Bio (ES)" value={tempPersonal.bio.es} onChange={(e) => setTempPersonal({ ...tempPersonal, bio: { ...tempPersonal.bio, es: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 h-24" />
                                <textarea placeholder="Bio (PT)" value={tempPersonal.bio.pt} onChange={(e) => setTempPersonal({ ...tempPersonal, bio: { ...tempPersonal.bio, pt: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 h-24" />
                                <textarea placeholder="Bio (EN)" value={tempPersonal.bio.en} onChange={(e) => setTempPersonal({ ...tempPersonal, bio: { ...tempPersonal.bio, en: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 h-24" />
                            </div>
                            <div className="space-y-2">
                                <textarea placeholder="Bio 2 (ES)" value={tempPersonal.bio2.es} onChange={(e) => setTempPersonal({ ...tempPersonal, bio2: { ...tempPersonal.bio2, es: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 h-24" />
                                <textarea placeholder="Bio 2 (PT)" value={tempPersonal.bio2.pt} onChange={(e) => setTempPersonal({ ...tempPersonal, bio2: { ...tempPersonal.bio2, pt: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 h-24" />
                                <textarea placeholder="Bio 2 (EN)" value={tempPersonal.bio2.en} onChange={(e) => setTempPersonal({ ...tempPersonal, bio2: { ...tempPersonal.bio2, en: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 h-24" />
                            </div>
                            {/* Social Links */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <input type="text" placeholder="GitHub URL" value={tempPersonal.github} onChange={(e) => setTempPersonal({ ...tempPersonal, github: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3" />
                                <input type="text" placeholder="LinkedIn URL" value={tempPersonal.linkedin} onChange={(e) => setTempPersonal({ ...tempPersonal, linkedin: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3" />
                                <input type="text" placeholder="Website URL" value={tempPersonal.website} onChange={(e) => setTempPersonal({ ...tempPersonal, website: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3" />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    onClick={savePersonalInfo}
                                    disabled={saving}
                                    className={`flex-1 bg-green-600 hover:bg-green-500 text-white py-2 rounded-lg transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {saving ? 'Guardando...' : t.save}
                                </button>
                                <button onClick={() => setEditingPersonal(false)} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded-lg transition-colors">{t.cancel}</button>
                            </div>
                        </div>
                    </div>
                )}



            </div>
        </header>
    );
}
